import { logger } from "@/lib/logger";
/**
 * CRASH-SAFE: Offline queue system for Africa's inconsistent connectivity
 * Stores failed API requests and retries when connection is restored
 * 
 * All operations wrapped in try-catch for maximum resilience
 * Failed items are preserved for user review/manual retry instead of being discarded
 */

import { 
  safeGetStorage, 
  safeSetStorage, 
  safeIsOnline, 
  safeDispatchEvent 
} from '@/lib/browserSafeUtils';
import { generateSyncId } from '@/utils/syncIdGenerator';

interface QueuedRequest {
  id: string;
  syncId: string; // Unique sync ID for idempotency
  url: string;
  method: string;
  body?: any;
  headers?: Record<string, string>;
  timestamp: number;
  retries: number;
  lastAttempt?: number;
  errorMessage?: string;
}

interface FailedRequest extends QueuedRequest {
  failedAt: number;
  finalError: string;
}

const QUEUE_KEY = 'offline-request-queue';
const FAILED_QUEUE_KEY = 'offline-failed-queue';
const MAX_RETRIES = 3;

class OfflineQueue {
  private queue: QueuedRequest[] = [];
  private failedQueue: FailedRequest[] = [];
  private isProcessing = false;

  constructor() {
    this.loadQueue();
    this.loadFailedQueue();
    this.setupListeners();
  }

  private loadQueue() {
    try {
      const stored = safeGetStorage(QUEUE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.queue = Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      logger.warn('[offlineQueue] Failed to load queue:', error);
      this.queue = [];
    }
  }

  private loadFailedQueue() {
    try {
      const stored = safeGetStorage(FAILED_QUEUE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.failedQueue = Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      logger.warn('[offlineQueue] Failed to load failed queue:', error);
      this.failedQueue = [];
    }
  }

  private saveQueue() {
    try {
      const success = safeSetStorage(QUEUE_KEY, JSON.stringify(this.queue));
      if (!success) {
        logger.error('[offlineQueue] CRITICAL: Failed to persist queue — localStorage may be full. Data at risk.');
        safeDispatchEvent('offline-storage-critical', { 
          message: 'Storage full — queued data may be lost. Please sync immediately.',
          queueSize: this.queue.length 
        });
      }
    } catch (error) {
      logger.warn('[offlineQueue] Failed to save queue:', error);
    }
  }

  private saveFailedQueue() {
    try {
      const success = safeSetStorage(FAILED_QUEUE_KEY, JSON.stringify(this.failedQueue));
      if (!success) {
        logger.error('[offlineQueue] CRITICAL: Failed to persist failed queue — localStorage may be full.');
      }
    } catch (error) {
      logger.warn('[offlineQueue] Failed to save failed queue:', error);
    }
  }

  private setupListeners() {
    // NOTE: The `online` event is now handled by the unified syncOrchestrator
    // to prevent all 5 sync mechanisms from firing simultaneously on reconnect.
    // Manual retry is still available via offlineQueue.retry().
  }

  // Note: generateSyncId is now imported from @/utils/syncIdGenerator

  /**
   * Add a failed request to the queue
   */
  add(url: string, options: RequestInit = {}, syncId?: string) {
    try {
      let bodyParsed: any = undefined;
      if (options.body && typeof options.body === 'string') {
        try {
          bodyParsed = JSON.parse(options.body);
        } catch {
          bodyParsed = options.body;
        }
      }

      const generatedSyncId = syncId || generateSyncId();
      
      // Check for duplicate sync IDs to prevent re-queuing
      const existingRequest = this.queue.find(r => r.syncId === generatedSyncId);
      if (existingRequest) {
        logger.debug(`[offlineQueue] Request with syncId ${generatedSyncId} already queued, skipping`);
        return;
      }

      const request: QueuedRequest = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        syncId: generatedSyncId,
        url,
        method: options.method || 'GET',
        body: bodyParsed,
        headers: options.headers as Record<string, string>,
        timestamp: Date.now(),
        retries: 0,
      };

      this.queue.push(request);
      this.saveQueue();
      
      logger.debug(`Added request to offline queue: ${url} (syncId: ${generatedSyncId})`);
      safeDispatchEvent('offline-queue-updated', { 
        count: this.queue.length,
        failedCount: this.failedQueue.length 
      });
    } catch (error) {
      logger.warn('[offlineQueue] Failed to add request:', error);
    }
  }

  /**
   * Process all queued requests
   */
  async processQueue() {
    if (this.isProcessing || !safeIsOnline()) {
      return;
    }

    this.isProcessing = true;
    const stillPending: QueuedRequest[] = [];

    try {
      for (const request of this.queue) {
        try {
          request.lastAttempt = Date.now();
          
          const response = await fetch(request.url, {
            method: request.method,
            headers: request.headers,
            body: request.body ? JSON.stringify(request.body) : undefined,
          });

          if (response.ok) {
            logger.debug(`Successfully processed queued request: ${request.url}`);
          } else {
            throw new Error(`Request failed with status ${response.status}`);
          }
        } catch (error: any) {
          logger.error(`Failed to process queued request: ${request.url}`, error);
          
          request.retries++;
          request.errorMessage = error.message || 'Unknown error';
          
          // Check if max retries reached
          if (request.retries >= MAX_RETRIES) {
            logger.error(`Max retries reached for request: ${request.url}`);
            // Move to failed queue instead of discarding
            const failedRequest: FailedRequest = {
              ...request,
              failedAt: Date.now(),
              finalError: error.message || 'Max retries exceeded',
            };
            this.failedQueue.push(failedRequest);
            this.saveFailedQueue();
            
            // Notify user about permanently failed item
            safeDispatchEvent('offline-queue-failed', { 
              request: failedRequest,
              message: `Failed to sync: ${request.url}` 
            });
          } else {
            stillPending.push(request);
          }
        }
      }

      this.queue = stillPending;
      this.saveQueue();
    } catch (error) {
      logger.warn('[offlineQueue] Error processing queue:', error);
    } finally {
      this.isProcessing = false;
      safeDispatchEvent('offline-queue-updated', { 
        count: this.queue.length,
        failedCount: this.failedQueue.length 
      });
    }
  }

  /**
   * Get current queue count - always returns a number
   */
  getCount(): number {
    try {
      return this.queue.length;
    } catch {
      return 0;
    }
  }

  /**
   * Get failed queue count
   */
  getFailedCount(): number {
    try {
      return this.failedQueue.length;
    } catch {
      return 0;
    }
  }

  /**
   * Get all failed requests for user review
   */
  getFailedRequests(): FailedRequest[] {
    try {
      return [...this.failedQueue];
    } catch {
      return [];
    }
  }

  /**
   * Retry a specific failed request
   */
  retryFailed(requestId: string): void {
    try {
      const index = this.failedQueue.findIndex(r => r.id === requestId);
      if (index === -1) return;

      const [request] = this.failedQueue.splice(index, 1);
      this.saveFailedQueue();

      // Reset retries and add back to main queue
      this.queue.push({
        ...request,
        retries: 0,
        errorMessage: undefined,
      });
      this.saveQueue();

      // Process immediately if online
      if (safeIsOnline()) {
        this.processQueue();
      }

      safeDispatchEvent('offline-queue-updated', { 
        count: this.queue.length,
        failedCount: this.failedQueue.length 
      });
    } catch (error) {
      logger.warn('[offlineQueue] Failed to retry request:', error);
    }
  }

  /**
   * Retry all failed requests
   */
  retryAllFailed(): void {
    try {
      for (const request of this.failedQueue) {
        this.queue.push({
          ...request,
          retries: 0,
          errorMessage: undefined,
        });
      }
      
      this.failedQueue = [];
      this.saveFailedQueue();
      this.saveQueue();

      if (safeIsOnline()) {
        this.processQueue();
      }

      safeDispatchEvent('offline-queue-updated', { 
        count: this.queue.length,
        failedCount: 0 
      });
    } catch (error) {
      logger.warn('[offlineQueue] Failed to retry all:', error);
    }
  }

  /**
   * Dismiss a failed request (remove without retrying)
   */
  dismissFailed(requestId: string): void {
    try {
      this.failedQueue = this.failedQueue.filter(r => r.id !== requestId);
      this.saveFailedQueue();
      safeDispatchEvent('offline-queue-updated', { 
        count: this.queue.length,
        failedCount: this.failedQueue.length 
      });
    } catch (error) {
      logger.warn('[offlineQueue] Failed to dismiss request:', error);
    }
  }

  /**
   * Clear all failed requests
   */
  clearFailed(): void {
    try {
      this.failedQueue = [];
      this.saveFailedQueue();
      safeDispatchEvent('offline-queue-updated', { 
        count: this.queue.length,
        failedCount: 0 
      });
    } catch (error) {
      logger.warn('[offlineQueue] Failed to clear failed queue:', error);
    }
  }

  /**
   * Clear the main queue
   */
  clear() {
    try {
      this.queue = [];
      this.saveQueue();
      safeDispatchEvent('offline-queue-updated', { 
        count: 0,
        failedCount: this.failedQueue.length 
      });
    } catch (error) {
      logger.warn('[offlineQueue] Failed to clear queue:', error);
    }
  }

  /**
   * Manually retry all queued requests
   */
  retry() {
    try {
      this.processQueue();
    } catch (error) {
      logger.warn('[offlineQueue] Failed to retry queue:', error);
    }
  }

  /**
   * Get statistics for admin dashboard
   */
  getStats(): {
    pending: number;
    failed: number;
    oldestPending: number | null;
    oldestFailed: number | null;
  } {
    try {
      const oldestPending = this.queue.length > 0 
        ? Math.min(...this.queue.map(r => r.timestamp))
        : null;
      const oldestFailed = this.failedQueue.length > 0 
        ? Math.min(...this.failedQueue.map(r => r.timestamp))
        : null;

      return {
        pending: this.queue.length,
        failed: this.failedQueue.length,
        oldestPending,
        oldestFailed,
      };
    } catch {
      return { pending: 0, failed: 0, oldestPending: null, oldestFailed: null };
    }
  }
}

// Create singleton instance safely
let _offlineQueueInstance: OfflineQueue | null = null;

function getOfflineQueue(): OfflineQueue {
  if (!_offlineQueueInstance) {
    try {
      _offlineQueueInstance = new OfflineQueue();
    } catch (error) {
      logger.warn('[offlineQueue] Failed to create instance:', error);
      // Return a no-op instance
      return {
        add: () => {},
        processQueue: async () => {},
        getCount: () => 0,
        getFailedCount: () => 0,
        getFailedRequests: () => [],
        retryFailed: () => {},
        retryAllFailed: () => {},
        dismissFailed: () => {},
        clearFailed: () => {},
        clear: () => {},
        retry: () => {},
        getStats: () => ({ pending: 0, failed: 0, oldestPending: null, oldestFailed: null }),
      } as unknown as OfflineQueue;
    }
  }
  return _offlineQueueInstance;
}

/**
 * Typed request data for consent forms and patient data
 * This provides backwards compatibility with the old queue API
 */
export type TypedRequestType = 'onboarding' | 'registration' | 'patient_data' | 'match_request' | 'consent_signature' | 'passport_export' | 'passport_import';

export interface TypedQueuedData {
  type: string;
  [key: string]: any;
}

/**
 * Read-only snapshot of pending requests (for status panels).
 * Returns shallow copies so callers cannot mutate internal state.
 */
export interface PendingRequestSnapshot {
  id: string;
  syncId: string;
  url: string;
  method: string;
  body?: any;
  timestamp: number;
  retries: number;
  lastAttempt?: number;
  errorMessage?: string;
}

// Export a proxy that safely accesses the singleton
export const offlineQueue = {
  add: (url: string, options?: RequestInit, syncId?: string) => getOfflineQueue().add(url, options, syncId),
  getPendingRequests: (): PendingRequestSnapshot[] => {
    try {
      // @ts-expect-error reach into private queue for read-only snapshot
      return [...(getOfflineQueue().queue ?? [])];
    } catch {
      return [];
    }
  },
  
  /**
   * Add typed data (backwards compatible with old queue API)
   * Converts typed data to a queued Supabase edge function request.
   * Includes apikey header for authentication.
   */
  addTyped: (type: TypedRequestType, data: TypedQueuedData) => {
    let endpoint: string;
    try {
      endpoint = getEndpointForType(type);
    } catch (err) {
      // Unmapped/dead types are dropped on the floor with a loud warning
      // rather than queued against a phantom URL.
      logger.error('[offlineQueue.addTyped] not enqueued:', err);
      return;
    }
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';
    getOfflineQueue().add(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ type, ...data }),
    });
  },
  
  processQueue: () => getOfflineQueue().processQueue(),
  getCount: () => getOfflineQueue().getCount(),
  getFailedCount: () => getOfflineQueue().getFailedCount(),
  getFailedRequests: () => getOfflineQueue().getFailedRequests(),
  retryFailed: (requestId: string) => getOfflineQueue().retryFailed(requestId),
  retryAllFailed: () => getOfflineQueue().retryAllFailed(),
  dismissFailed: (requestId: string) => getOfflineQueue().dismissFailed(requestId),
  clearFailed: () => getOfflineQueue().clearFailed(),
  clear: () => getOfflineQueue().clear(),
  retry: () => getOfflineQueue().retry(),
  getStats: () => getOfflineQueue().getStats(),
};

/**
 * Map typed request types to their Supabase edge function endpoints.
 * These MUST be real, reachable URLs — phantom paths cause infinite retry loops
 * that bloat the offline queue and never clear.
 *
 * Verified against supabase/functions/ on 2026-05-17:
 *   - patient-signup            (exists) — onboarding / registration
 *   - process-consent-signature (exists) — consent_signature (same payload contract)
 *   - offline-sync              (exists) — generic dispatcher for patient_data (ecoa_response)
 *
 * Dead types (no callers in repo): match_request, passport_export, passport_import.
 * These throw at enqueue time below so they cannot silently bloat the queue.
 */
function getEndpointForType(type: TypedRequestType): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const fnBase = `${supabaseUrl}/functions/v1`;

  switch (type) {
    case 'onboarding':
    case 'registration':
      return `${fnBase}/patient-signup`;
    case 'consent_signature':
      return `${fnBase}/process-consent-signature`;
    case 'patient_data':
      return `${fnBase}/offline-sync`;
    case 'match_request':
    case 'passport_export':
    case 'passport_import':
      throw new Error(
        `[offlineQueue] Typed request '${type}' has no edge function endpoint. ` +
        `Refusing to enqueue — would bloat the offline queue with permanent 404s.`,
      );
    default: {
      const exhaustive: never = type;
      throw new Error(`[offlineQueue] Unknown typed request: ${exhaustive}`);
    }
  }
}
